import React from 'react';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import '../Workflow.scss'
import { useDispatch } from 'react-redux'
import TicketActions from '../../../../../store/ticketRedux';
import WorkflowTask from '../../../../../components/library/workflow-tasks/WorkflowTask'
import classnames from 'classnames'
import { useTaskStatus } from '../../hooks/useTaskStatusHook';
import { WorkflowTasks } from '../../../services/WorkflowTasks';
import { TaskStatus } from '../../../services/TaskStatus';
import WorkflowTaskDescriptionWithHelp from '../../../../../components/library/workflow-tasks/WorkflowTaskDescriptionWithHelp';
import VerticalNameChildren from '../../../../../components/library/layout/VerticalNameChildren';
import { useInterviewInfo } from '../../hooks/useInterviewInfoHook';
import LoadingTextValue from '../../../../../components/library/layout/LoadingTextValue';
import HrbTranslate from '../../../../../components/language/HrbTranslate';
import DateTime from '../../../../../components/library/common/DateTime';
import EmdashText from '../../../../../components/library/common/EmdashText';
import { shallowEqual } from 'react-redux';
import BlockSignalLink from '../../../../../components/library/common/BlockSignalLink';
import Button from '../../../../../components/library/buttons/Button';
import ViewDownloadDocument from '../../../../../components/library/documents/ViewDownloadDocument';
import WorkflowTaskResetModal from '../../../../../components/library/workflow-tasks/WorkflowTaskResetModal';
import { resetAuthorizeForTicket } from '../../../../../services/tickets/ticketResetter';
import { useTicketState } from '../../hooks/useTicketStateHook';
import { trackEvent, HeapEvents } from '../../../../../services/util/AnalyticsUtil';
import Lang from '../../../../../components/language/services/Lang'
import { supportsAuthorizationReset, supportsDocumentReview } from 'strategies/TicketTypeStrategy';
import { isCoupledReturn, isQuebecResident } from 'strategies/ClientTypeStrategy';
import { getValidTaskCompletionData, getCompletionData } from 'strategies/TaskCompletionStrategy';
import { isComplete, isFailed } from 'strategies/TaskStatusStrategy';

const AuthorizationTask = (props) => {
    const dispatch = useDispatch();
    const ticketIsOpen = useTicketState(props.ticket);
    const ticketType = props.ticket.ticketType.identifier.toUpperCase();  
    const [returnType, , province, primaryName, spouseName] = useInterviewInfo(props.ticket);
    const [preparePaymentStatus] = useTaskStatus(props.ticket, [WorkflowTasks.PAYMENT_PREPARED]);

    let tasks = isCoupledReturn(returnType) ? (isQuebecResident(province) ?
        ([WorkflowTasks.AUTHORIZE_CRA, WorkflowTasks.AUTHORIZE_CRA_SPOUSE, WorkflowTasks.AUTHORIZE_RQ, WorkflowTasks.AUTHORIZE_RQ_SPOUSE]) :
        ([WorkflowTasks.AUTHORIZE_CRA, WorkflowTasks.AUTHORIZE_CRA_SPOUSE])) :
        (isQuebecResident(province) ? ([WorkflowTasks.AUTHORIZE_CRA, WorkflowTasks.AUTHORIZE_RQ]) :
            ([WorkflowTasks.AUTHORIZE_CRA]))
        
    const [baseStatus, taskCompletions] = useTaskStatus(props.ticket, tasks);

    const [selectedPartner, setSelectedPartner] = useState('PRIMARY')

    // for overriding status on panel
    const [status, setStatus] = useState(TaskStatus.NOT_STARTED);
    // track individual authorization for each filer
    const [primaryStatus, setPrimaryStatus] = useState('')
    const [primaryRqStatus, setPrimaryRqStatus] = useState('')
    const [spouseStatus, setSpouseStatus] = useState('')
    const [spouseRqStatus, setSpouseRqStatus] = useState('')

    // task completion data for each filer
    const [primaryCompletion, setPrimaryCompletion] = useState({})
    const [primaryRqCompletion, setPrimaryRqCompletion] = useState({})
    const [spouseCompletion, setSpouseCompletion] = useState({})
    const [spouseRqCompletion, setSpouseRqCompletion] = useState({})
    const [resetModalShowing, setResetModalShowing] = useState(false)
    const [resetting, setResetting] = useState(false)
    const [primaryEafFileId, setPrimaryEafFileId] = useState({})
    const [spouseEafFileId, setSpouseEafFileId] = useState({})
    const [primaryMr69FileId, setPrimaryMr69FileId] = useState({})
    const [spouseMr69FileId, setSpouseMr69FileId] = useState({})

    const handleShowModal = (id) => {
        if (id === 'primaryResetButton')
            setSelectedPartner('PRIMARY')
        else if (id === 'spouseResetButton')
            setSelectedPartner('SPOUSE')

        setResetModalShowing(true)
    }

    const handleHideModal = () => {
        setResetModalShowing(false);        
    }

    const handleResetAuthorization = async () => {
        setResetting(true);
        if (await resetAuthorizeForTicket(props.ticket.id, selectedPartner !== 'SPOUSE')) {
            dispatch(TicketActions.getTicket(props.ticket.id))
            trackEvent(selectedPartner !== 'SPOUSE' ? HeapEvents.AUTH_RESET_PRIMARY : HeapEvents.AUTH_RESET_SPOUSE, props.ticket.id)
        } 
        setResetting(false);
        setResetModalShowing(false);
    }

    useEffect(() => {
        let tcs = taskCompletions || []

        let primary = tcs.find(x => getValidTaskCompletionData(x, WorkflowTasks.AUTHORIZE_CRA))
        let primaryRq = tcs.find(x => getValidTaskCompletionData(x, WorkflowTasks.AUTHORIZE_RQ))

        let primaryData = getCompletionData(primary);
        if (primary) {
            setPrimaryStatus(primary.status || '')
        } else {
            setPrimaryStatus('')
        }
        if (!shallowEqual(primaryData, primaryCompletion)) { setPrimaryCompletion(primaryData) }

        let primaryRqData = getCompletionData(primaryRq);
        if (primaryRq) {
            setPrimaryRqStatus(primaryRq.status || '')
        } else {
            setPrimaryRqStatus('')
        }
        if (!shallowEqual(primaryRqData, primaryRqCompletion)) { setPrimaryRqCompletion(primaryRqData) }

        let spouse = tcs.find(x => getValidTaskCompletionData(x, WorkflowTasks.AUTHORIZE_CRA_SPOUSE));
        let spouseRq = tcs.find(x => getValidTaskCompletionData(x, WorkflowTasks.AUTHORIZE_RQ_SPOUSE));

        let spouseData = getCompletionData(spouse);        
        if (spouse) {
            setSpouseStatus(spouse.status || '')
        } else {
            setSpouseStatus('')
        }
        if (!shallowEqual(spouseData, spouseCompletion)) { setSpouseCompletion(spouseData) }

        let spouseRqData = getCompletionData(spouseRq);
        if (spouseRq) {
            setSpouseRqStatus(spouseRq.status || '')
        } else {
            setSpouseRqStatus('')
        }
        if (!shallowEqual(spouseRqData, spouseRqCompletion)) { setSpouseRqCompletion(spouseRqData) }
    }, [taskCompletions])

    useEffect(() => {
        if (( isComplete(primaryStatus) && !primaryCompletion.AuthorizationDate) ||
            (isComplete(spouseStatus) && !spouseCompletion.AuthorizationDate) ||
            (isComplete(primaryRqStatus) && !primaryRqCompletion.AuthorizationDate) ||
            (isComplete(spouseRqStatus) && !spouseRqCompletion.AuthorizationDate)) {
            setStatus(TaskStatus.ERROR)
        } else if ((primaryStatus && !primaryCompletion.AuthorizationDate) ||
            (primaryRqStatus && !primaryRqCompletion.AuthorizationDate) ||
            (spouseStatus && !spouseCompletion.AuthorizationDate) ||
            (spouseRqStatus && !spouseRqCompletion.AuthorizationDate)) {
            setStatus(TaskStatus.IN_PROGRESS)
        
        } else if (primaryStatus && primaryCompletion.AuthorizationDate && !spouseStatus && isCoupledReturn(returnType)) {
            setStatus(TaskStatus.IN_PROGRESS)
        } else if (spouseStatus && spouseCompletion.AuthorizationDate && !primaryStatus && isCoupledReturn(returnType)) {
            setStatus(TaskStatus.IN_PROGRESS)
        }else {
            setStatus(baseStatus)
        }
    }, [baseStatus, primaryStatus, spouseStatus])

    const handlePrimaryViewDownloadFail = () => {
        setPrimaryEafFileId(null)
    }
    const handleSpouseViewDownloadFail = () => {
        setSpouseEafFileId(null)
    }

    const handlePrimaryRqViewDownloadFail = () => {
        setPrimaryMr69FileId(null)
    }

    const handleSpouseRqViewDownloadFail = () => {
        setSpouseMr69FileId(null)
    }

    const renderCompletionStatus = (compData, completed, partner) => {
        if (!completed) {
            return <p className='p1 medium-weight'><EmdashText /></p>
        } else {
            switch (compData.AuthorizationMethod) {
                case 'MyAccount':
                    return <p className='p1 medium-weight'><HrbTranslate text="AuthorizationTask.MyAccount" /></p>
                case 'EAF':                    
                    return partner === 'Primary' ? primaryEafFileId ? <ViewDownloadDocument ticketId={props.ticket.id} documentType='EAFPrimary' onViewDownloadFail={handlePrimaryViewDownloadFail} /> : <p className='p2 error'><HrbTranslate text="AuthorizationTask.ErrorEafPdf" params={{ BlockSignal: <BlockSignalLink /> }} /></p> : 
                        spouseEafFileId ? <ViewDownloadDocument ticketId={props.ticket.id} documentType='EAFSpouse' onViewDownloadFail={handleSpouseViewDownloadFail} /> : <p className='p2 error'><HrbTranslate text="AuthorizationTask.ErrorEafPdf" params={{ BlockSignal: <BlockSignalLink /> }} /></p>
                case 'MR69':
                    return partner === 'Primary' ? primaryMr69FileId ? <ViewDownloadDocument ticketId={props.ticket.id} documentType='MR69Primary' onViewDownloadFail={handlePrimaryRqViewDownloadFail} /> : <p className='p2 error'><HrbTranslate text="AuthorizationTask.ErrorMr69Pdf" params={{ BlockSignal: <BlockSignalLink /> }} /></p> :
                        spouseMr69FileId ? <ViewDownloadDocument ticketId={props.ticket.id} documentType='MR69Spouse' onViewDownloadFail={handleSpouseRqViewDownloadFail} /> : <p className='p2 error'><HrbTranslate text="AuthorizationTask.ErrorMr69Pdf" params={{ BlockSignal: <BlockSignalLink /> }} /></p>
                case 'Direct':
                    return <p className='p1 medium-weight'><HrbTranslate text="AuthorizationTask.Direct" /></p>
                case 'SkipAuth':
                    return <p className='p1 medium-weight'><HrbTranslate text="AuthorizationTask.SkipAuth" /></p>
                default:
                    return <p className='p1 medium-weight'><HrbTranslate text="Tickets.Tasks.TaskStatus.NotStarted" /></p>
            }
        }
    }

    const renderTimestamp = (compData, complete, compRqData, completeRq, province) => {
        if ((complete && !compData.AuthorizationDate) && (completeRq && !compRqData.AuthorizationDate)) {
            return <HrbTranslate text='ConfirmIdTask.ErrorTimestamp' params={{ BlockSignal: <BlockSignalLink /> }} className='p2 error' />
        } else if ((complete && compData.AuthorizationDate) && (province !== 'QC')) {
            return <p className='p1 medium-weight'><DateTime dateAndTime value={compData.AuthorizationDate} /></p>
        } else if ((complete && compData.AuthorizationDate) && (completeRq && compRqData.AuthorizationDate)) {
            return <p className='p1 medium-weight'><DateTime dateAndTime value={compRqData.AuthorizationDate} /></p>
        } else {
               return <p className='p1 medium-weight'><EmdashText /></p>
        }
    }

    const renderErrorStatus = () => {
        if (isFailed(status)) {
            let primaryFailed =  isFailed(primaryStatus);
            let spouseFailed = isFailed(spouseCompletion.Status);

            let errorKey = spouseFailed && primaryFailed ?
                'ConfirmIdTask.ErrorMultipleVerificationFailure' :
                'ConfirmIdTask.ErrorSingleVerificationFailure'
            let params = spouseFailed && primaryFailed ?
                { PrimaryName: primaryName, SpouseName: spouseName } :
                { Name: primaryFailed ? primaryName : spouseName }

            return <div className='columns'>
                <div className='column is-8 is-offset-1'>
                    <HrbTranslate text={errorKey} params={params} className='p2 error' />
                </div>
            </div>
        }
    }

    const createLink = (urlKey, titleKey) => (
        <a href={Lang.getCurrentValue(urlKey)} target='_blank' rel='noreferrer noopener'>
            <HrbTranslate text={titleKey} />
        </a>
    )

    return (
        <WorkflowTask status={status} taskId={WorkflowTasks.AUTHORIZE_CRA}>
            <div className='columns'>                
               <WorkflowTaskDescriptionWithHelp
                    helpKey='0ProSupport/VDOPro/Authorization.htm'
                    descriptions={['AuthorizationTask.Description1']}
                    params={['AuthorizationTask.Description1LinkText']} />                
            </div>
            <div className='columns prep-section'>
                <div className='column is-offset-1 is-8'>
                    <p className='p2'>
                        <HrbTranslate text={'AuthorizationTask.Description2'}
                        params={{
                            CRARules: createLink('AuthorizationTask.Description1Link', 'AuthorizationTask.Description1LinkText')
                            }} />
                    </p>
                </div>
            </div>
            <WorkflowTaskResetModal
                isOpen={resetModalShowing} status='InProgress' onCancel={handleHideModal}
                onReset={handleResetAuthorization} titleKey="AuthorizationTask.ResetModal.Title"
                resetButtonKey="AuthorizationTask.Reset"
                contentKeys={supportsDocumentReview(ticketType) ? ['AuthorizationTask.ResetModal.ContentDR'] : ['AuthorizationTask.ResetModal.Content1', 'AuthorizationTask.ResetModal.Content2']} />

            <div className='columns'>
                <div className={classnames('column is-offset-1', { 'is-4':  isCoupledReturn(returnType), 'is-8': !isCoupledReturn(returnType) })}>
                    <h4><LoadingTextValue loading={!props.ticket} value={primaryName} /></h4>
                </div>
                {isCoupledReturn(returnType) &&
                    <div className='column is-4'>
                        <h4><LoadingTextValue loading={!props.ticket} value={spouseName} /></h4>
                    </div>
                }
                <div className='column is-3' />
            </div>
            <div className='columns'>
                <div className={classnames('column is-offset-1', { 'is-4': isCoupledReturn(returnType), 'is-8': !isCoupledReturn(returnType) })}>
                    <VerticalNameChildren
                        textKey="AuthorizationTask.AuthorizedOn"
                        loading={!props.ticket}>
                        {renderTimestamp(primaryCompletion, isComplete(primaryStatus), primaryRqCompletion, isComplete(primaryRqStatus), province)}
                    </VerticalNameChildren>
                </div>
                {returnType === 'coupled' &&
                    <div className='column is-4'>
                        <VerticalNameChildren
                        textKey="AuthorizationTask.AuthorizedOn"
                        loading={!props.ticket}>
                        {renderTimestamp(spouseCompletion, isComplete(spouseStatus), spouseRqCompletion, isComplete(spouseRqStatus), province)}
                        </VerticalNameChildren>
                    </div>
                }
                <div className='column is-3' />
            </div>
            <div className='columns'>
                <div className='column is-1' />
                <div className={classnames('column', { 'is-4':  isCoupledReturn(returnType), 'is-8': !isCoupledReturn(returnType) })}>
                    <VerticalNameChildren
                        textKey="AuthorizationTask.CRA"
                        loading={!props.ticket}>
                        {renderCompletionStatus(primaryCompletion, isComplete(primaryStatus), 'Primary')}
                    </VerticalNameChildren>
                </div>
                {returnType === 'coupled' &&
                    <div className='column is-4'>
                        <VerticalNameChildren
                        textKey="AuthorizationTask.CRA"
                            loading={!props.ticket}>
                        {renderCompletionStatus(spouseCompletion, isComplete(spouseStatus), 'Spouse')}
                        </VerticalNameChildren>
                    </div>
                }
                <div className='column is-3' />
            </div>
            { isQuebecResident(province) &&
                <div className='columns'>
                    <div className='column is-1' />
                    <div className={classnames('column', { 'is-4': isCoupledReturn(returnType), 'is-8': !isCoupledReturn(returnType) })}>
                        <VerticalNameChildren
                            textKey="AuthorizationTask.RQ"
                            loading={!props.ticket}>
                            {renderCompletionStatus(primaryRqCompletion, isComplete(primaryRqStatus), 'Primary')}
                        </VerticalNameChildren>
                    </div>
                    {returnType === 'coupled' &&
                        <div className='column is-4'>
                            <VerticalNameChildren
                                textKey="AuthorizationTask.RQ"
                                loading={!props.ticket}>
                                {renderCompletionStatus(spouseRqCompletion, isComplete(spouseRqStatus), 'Spouse')}
                            </VerticalNameChildren>
                        </div>
                    }
                    <div className='column is-3' />
                </div>}
            { !isComplete(preparePaymentStatus) &&
                <div className='columns'>
                    <div className='column is-1' />
                    <div className='column is-3'>
                        { isComplete(primaryStatus) && supportsAuthorizationReset(ticketType) &&
                            <Button
                                id='primaryResetButton'
                                elementId='ticket-cancel-btn'
                                labelKey='AuthorizationTask.Reset'
                                buttonStyles='secondary'
                                disabled={!ticketIsOpen}
                                loading={resetting}
                                onClick={(id) => handleShowModal('primaryResetButton')} />}
                    </div>
                    <div className='column is-1' />
                    { returnType === 'coupled' && isComplete(spouseStatus) && supportsAuthorizationReset(ticketType) &&
                        <div className='column is-4'>
                            <Button
                                disabled={!ticketIsOpen}
                                loading={resetting}
                                id='spouseResetButton'
                                elementId='ticket-cancel-btn'
                                labelKey='AuthorizationTask.Reset'
                                buttonStyles='secondary'
                                onClick={(id) => handleShowModal('spouseResetButton')} />
                        </div>
                    }
                </div>}
                <div className='columns prep-section'>
                    <div className='column is-offset-1 is-8'>
                        <p className='p2'>
                            {supportsDocumentReview(ticketType) ? (
                                <HrbTranslate text='AuthorizationTask.Description3DR'
                                    params={{
                                        RAMTemplate: createLink('AuthorizationTask.Description3LinkDR', 'AuthorizationTask.Description3LinkTextDR')
                                    }} />
                            ) : (
                                <HrbTranslate text='AuthorizationTask.Description3'
                                    params={{
                                        RAMTemplate: createLink('AuthorizationTask.Description3Link1', 'AuthorizationTask.Description3Link1Text'),
                                        RACErrorWorkarounds: createLink('AuthorizationTask.Description3Link2', 'AuthorizationTask.Description3Link2Text')
                                    }} />
                            )}
                        </p>
                    </div>
                </div>

            {renderErrorStatus()}
        </WorkflowTask>
    );
}

AuthorizationTask.whyDidYouRender = false;

AuthorizationTask.propTypes = {
    ticket: PropTypes.object
}

export default AuthorizationTask